import React from 'react';

import { useMedia } from 'react-use';
import cx from 'classnames';
import { MEDIA_QUERY_DESKTOP_ROVER, MEDIA_QUERY_MOBILE_L } from '../../../utils/von-dyck-media-queries';
import { useEditor, useRobotSelector } from '../context';
import RobotInfo from '../__info/robot__info';
import { Light, LightV2 } from '../__code-container/robot__code-container';
import { RobotFieldPane } from '../__field-pane/robot__field-pane';
import { EDITOR_TYPES } from '../robot.types';
import SplitView from '../../split-view/split-view';
import Text from '../../text/text';
import { UI_KIT_TEXT_TYPOGRAPHY } from '../../constants';
import RobotOnboarding from '../__code-light-v2/robot__code-light-onboarding';
import { RobotViewPython } from '../_view/_python/robot_view_python';
import { RobotViewBlocklyPython } from '../_view/_blockly-python/robot_view_blockly-python';
import { ROBOT_FIELD_MIN_HEIGHT, ROBOT_FIELD_MIN_WIDTH } from '../consts';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './robot__content.module.scss';

const RobotContentComponent = () => {
    const editor = useEditor();
    const teacherMode = useRobotSelector((store) => store.teacherMode);
    const isMobile = useMedia(MEDIA_QUERY_MOBILE_L);
    const isRoverDesktop = useMedia(MEDIA_QUERY_DESKTOP_ROVER);

    switch (editor?.editor) {
        case EDITOR_TYPES.PYTHON:
            return <RobotViewPython />;
        case EDITOR_TYPES.LIGHT_PYTHON:
            return (
                <SplitView
                    className={cx(styles.robot, 'qa_robot', { [styles.mobile]: isMobile })}
                    direction={isMobile ? 'column' : 'row'}
                    initialSizes={isMobile ? [1, 2] : undefined}
                    minSizes={
                        isMobile ? [ROBOT_FIELD_MIN_HEIGHT, ROBOT_FIELD_MIN_HEIGHT * 2] : [ROBOT_FIELD_MIN_WIDTH, 660]
                    }
                >
                    <RobotFieldPane />
                    <div className={cx(styles.right, 'qa_robot-editor')}>
                        <div
                            className={cx(
                                styles.code,
                                { [styles.code_teacher]: teacherMode },
                                'qa_robot-light-python-editor'
                            )}
                        >
                            <div style={{ padding: isMobile ? 0 : '0 100px 16px 20px' }}>
                                <div style={teacherMode ? { pointerEvents: 'none' } : undefined}>
                                    <RobotInfo showHelp={false} />
                                </div>
                            </div>
                            <Light />
                        </div>
                    </div>
                </SplitView>
            );
        case EDITOR_TYPES.CODE_LIGHT_V2:
            return (
                <SplitView
                    className={cx(styles.robot, 'qa_robot', { [styles.codeLightV2_desktop]: isRoverDesktop })}
                    direction={isRoverDesktop ? 'row' : 'column'}
                    disableResizable={isRoverDesktop}
                    extendedResizerActiveArea={true}
                    initialSizes={isRoverDesktop ? [1, 0] : [1, 2]} // todo: EDUCATION-31276: не пересчитывает при ресайзе
                    minSizes={isRoverDesktop ? [290, 464] : [ROBOT_FIELD_MIN_WIDTH, ROBOT_FIELD_MIN_HEIGHT * 2]}
                >
                    <RobotFieldPane shouldShowV2Interface={true} />
                    <div className={cx(styles.right, styles.codeLightV2_right, 'qa_robot-editor')}>
                        <div
                            className={cx(
                                styles.code,
                                { [styles.code_teacher]: teacherMode },
                                'qa_robot-light-python-editor'
                            )}
                        >
                            <div style={teacherMode ? { pointerEvents: 'none' } : undefined}>
                                <Text
                                    typography={
                                        isRoverDesktop
                                            ? UI_KIT_TEXT_TYPOGRAPHY.headlineMMedium
                                            : UI_KIT_TEXT_TYPOGRAPHY.paragraphXLMedium
                                    }
                                >
                                    <RobotInfo mix={styles.codeLightV2_info} showHelp={false} showTitle={false} />
                                </Text>
                            </div>
                            <LightV2 />
                        </div>
                    </div>
                    <RobotOnboarding />
                </SplitView>
            );
        case EDITOR_TYPES.BLOCKLY_PYTHON:
            return <RobotViewBlocklyPython />;
        default:
            return null;
    }
};

export const RobotContent = React.memo(RobotContentComponent);
